import { APIError } from '../../utils/errors.js';
import { MOCK_SURVEYS } from '../mock-data/index.js';
import { delay, getDeviceInfo } from '../utils/helpers.js';

export class SurveyService {
	constructor(baseAPI) {
		this.api = baseAPI;
	}

	async getActiveSurveys(context = {}) {
		await this.api._ensureSession();

		if (this.api.mock) {
			await delay(200);
			return { success: true, data: MOCK_SURVEYS };
		}

		const respondent = this._getRespondentContext(context);

		const params = {
			url:
				context.url ||
				(typeof window !== 'undefined' ? window.location.href : ''),
			...getDeviceInfo(),
			...(respondent.respondent_id && {
				respondent_id: respondent.respondent_id,
			}),
			...(respondent.email && { email: respondent.email }),
			...(context.includeEligibility !== undefined && {
				include_eligibility: context.includeEligibility,
			}),
			...(context.userProperties && {
				user_properties: context.userProperties,
			}),
		};

		return this.api._handleAuthRetry(async () => {
			const endpoint = this.api._getEndpointWithParams(
				'/widget/surveys/active',
				params
			);
			return await this.api._makeRequest(endpoint, {
				method: 'GET',
				headers: { Authorization: `Bearer ${this.api.sessionToken}` },
			});
		});
	}

	_getRespondentContext(context = {}) {
		const userContext =
			typeof this.api.getUserContext === 'function'
				? this.api.getUserContext() || {}
				: {};

		return {
			respondent_id:
				context.respondentId || context.userId || userContext.user_id || null,
			email: context.email || userContext.email || null,
		};
	}

	async submitSurveyResponse(surveyId, responseData) {
		if (!surveyId) throw new APIError(400, 'Survey ID is required');

		await this.api._ensureSession();

		if (this.api.mock) {
			await delay(300);
			return {
				success: true,
				data: {
					id: 'mock_response_' + Date.now(),
					survey_id: surveyId,
					...responseData,
				},
				message: 'Survey response submitted successfully!',
			};
		}

		const respondent = this._getRespondentContext(responseData);

		const payload = {
			rating: responseData.rating,
			feedback: responseData.feedback || '',
			answers: responseData.answers || {},
			...(respondent.respondent_id && {
				respondent_id: respondent.respondent_id,
			}),
			...(respondent.email && { email: respondent.email }),
		};

		return this.api._handleAuthRetry(async () => {
			return await this.api._makeRequest(
				`/widget/surveys/${surveyId}/responses`,
				{
					method: 'POST',
					body: JSON.stringify(payload),
					headers: {
						'Content-Type': 'application/json',
						Authorization: `Bearer ${this.api.sessionToken}`,
					},
				}
			);
		});
	}

	async dismissSurvey(surveyId) {
		if (!surveyId) throw new APIError(400, 'Survey ID is required');

		await this.api._ensureSession();

		if (this.api.mock) {
			await delay(100);
			return { success: true, message: 'Survey dismissed successfully' };
		}

		return this.api._handleAuthRetry(async () => {
			return await this.api._makeRequest(
				`/widget/surveys/${surveyId}/dismiss`,
				{
					method: 'POST',
					headers: { Authorization: `Bearer ${this.api.sessionToken}` },
				}
			);
		});
	}
}
